import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, IconButton, TextField, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Autocomplete } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Save as SaveIcon, Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS, SAP_TRANSITIONS } from '../theme/sapDesignSystem';
import { toast } from 'sonner';
import { handleApiError } from '../api/errorHandling';
import { createJobSetting, deleteJobSetting, fetchJobSettingKeys, fetchJobSettings, updateJobSetting, type JobSettingRow } from '../api/features/jobSetting/jobSettingApi';

export default function JobSetting({ clientCode, jobDetailId, clientId, allowEdit = true }: { clientCode: string; jobDetailId: string; clientId: number; allowEdit?: boolean; }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<JobSettingRow[]>([]);
  const [keys, setKeys] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [addOpen, setAddOpen] = useState(false);
  const [newKey, setNewKey] = useState<string>('');
  const [newValue, setNewValue] = useState<string>('');
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [saveConfirmRow, setSaveConfirmRow] = useState<JobSettingRow | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmRow, setDeleteConfirmRow] = useState<JobSettingRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const parsedJobDetailId = useMemo(() => Number(jobDetailId), [jobDetailId]);

  const load = async () => {
    setLoading(true);
    try {
      const [list, allKeys] = await Promise.all([
        fetchJobSettings({ clientCode, jobDetailId: parsedJobDetailId }),
        fetchJobSettingKeys({ clientCode }),
      ]);
      setRows(Array.isArray(list) ? list.filter(r => Array.isArray(r) && r.length >= 4) : []);
      setKeys(allKeys || []);
    } catch (e) {
      handleApiError(e, 'Failed to load job settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientCode && jobDetailId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientCode, jobDetailId]);

  const startEdit = (row: JobSettingRow) => {
    setEditingId(row[0]);
    setEditValue(row[2] || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const saveEdit = async (row: JobSettingRow) => {
    try {
      const payload = {
        value: editValue,
        key: row[3],
        jobDetailId: parsedJobDetailId,
        clientId: clientId,
        id: row[0],
      };
      const res = await updateJobSetting({ clientCode, payload });
      if (res?.success) {
        toast.success('Job setting updated');
        await load();
        cancelEdit();
        setSaveConfirmOpen(false);
        setSaveConfirmRow(null);
      }
    } catch (e) {
      handleApiError(e, 'Update failed');
    }
  };

  const openSaveConfirm = (row: JobSettingRow) => {
    setSaveConfirmRow(row);
    setSaveConfirmOpen(true);
  };

  const confirmSaveEdit = () => {
    if (saveConfirmRow) saveEdit(saveConfirmRow);
  };

  const openDeleteConfirm = (row: JobSettingRow) => {
    setDeleteConfirmRow(row);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmRow) return;
    setDeleting(true);
    try {
      await deleteJobSetting({ clientCode, id: deleteConfirmRow[0] });
      toast.success('Job setting deleted');
      await load();
      setDeleteConfirmOpen(false);
      setDeleteConfirmRow(null);
      if (editingId === deleteConfirmRow[0]) cancelEdit();
    } catch (e) {
      handleApiError(e, 'Delete is not available yet. Backend is in development.');
    } finally {
      setDeleting(false);
    }
  };

  const openAdd = () => {
    setNewKey('');
    setNewValue('');
    setAddOpen(true);
  };

  const closeAdd = () => {
    setAddOpen(false);
  };

  const confirmAdd = async () => {
    if (!newKey) {
      toast.error('Please select a key');
      return;
    }
    try {
      const payload = {
        value: newValue,
        key: newKey,
        jobDetailId: parsedJobDetailId,
        clientId: clientId,
      };
      const res = await createJobSetting({ clientCode, payload });
      if (res?.success) {
        toast.success('Job setting created');
        await load();
        closeAdd();
      }
    } catch (e) {
      handleApiError(e, 'Create failed');
    }
  };

  return (
    <Card sx={{ 
      borderRadius: SAP_SPACING.component.borderRadius,
      boxShadow: SAP_SHADOWS.card,
      backgroundColor: SAP_COLORS.sapWhite,
      border: `1px solid ${SAP_COLORS.sapGray2}`,
      fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
    }}>
      <CardContent sx={{ 
        p: { xs: SAP_SPACING.md, sm: SAP_SPACING.lg },
        '&:last-child': {
          paddingBottom: { xs: SAP_SPACING.md, sm: SAP_SPACING.lg },
        }
      }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Box sx={{ ml: 'auto' }}>
            {allowEdit && (
            <Tooltip title="Add Job Setting">
              <span>
                <IconButton 
                  onClick={openAdd} 
                  disabled={loading} 
                  sx={{ 
                    color: SAP_COLORS.sapBlue,
                    backgroundColor: SAP_COLORS.sapLightBlue,
                    borderRadius: SAP_SPACING.component.borderRadius,
                    boxShadow: SAP_SHADOWS.sm,
                    '&:hover': {
                      backgroundColor: SAP_COLORS.sapBlue,
                      color: SAP_COLORS.sapWhite,
                      transform: 'translateY(-2px)',
                      boxShadow: SAP_SHADOWS.md,
                    },
                    '&:disabled': {
                      backgroundColor: SAP_COLORS.sapGray2,
                      color: SAP_COLORS.sapGray4,
                      boxShadow: 'none',
                      transform: 'none',
                    },
                    transition: SAP_TRANSITIONS.fast,
                  }} 
                  size="large"
                >
                  <AddIcon sx={{ fontSize: 28 }} />
                </IconButton>
              </span>
            </Tooltip>
            )}
          </Box>
        </Box>

        <Box sx={{ 
          border: `1px solid ${SAP_COLORS.sapGray2}`, 
          borderRadius: SAP_SPACING.component.borderRadius,
          boxShadow: SAP_SHADOWS.sm,
          overflow: 'hidden',
        }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' }, 
            alignItems: 'center', 
            px: SAP_SPACING.md, 
            py: SAP_SPACING.lg, 
            backgroundColor: SAP_COLORS.sapPaleBlue, 
            borderBottom: `1px solid ${SAP_COLORS.sapGray2}` 
          }}>
            <Typography sx={{ 
              color: SAP_COLORS.sapGray7, 
              fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
              fontSize: SAP_TYPOGRAPHY.fontSize.sm,
              fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
            }}>
              Key
            </Typography>
            <Typography sx={{ 
              color: SAP_COLORS.sapGray7, 
              fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
              fontSize: SAP_TYPOGRAPHY.fontSize.sm,
              fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
              display: { xs: 'none', sm: 'block' } 
            }}>
              Value
            </Typography>
            <span />
          </Box>
          {rows.map((row, idx) => {
            const id = row[0];
            const value = row[2];
            const keyName = row[3];
            const isEditing = editingId === id;
            return (
              <Box 
                key={id} 
                sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' }, 
                  alignItems: 'center', 
                  px: SAP_SPACING.md, 
                  py: SAP_SPACING.lg, 
                  borderTop: idx === 0 ? 'none' : `1px solid ${SAP_COLORS.sapGray2}`, 
                  rowGap: { xs: SAP_SPACING.sm, sm: 0 }, 
                  backgroundColor: SAP_COLORS.sapWhite,
                  transition: SAP_TRANSITIONS.fast,
                  '&:hover': { 
                    backgroundColor: SAP_COLORS.sapGray1,
                    transform: 'translateY(-1px)',
                    boxShadow: SAP_SHADOWS.sm,
                  },
                  '&:last-child': {
                    borderBottomLeftRadius: SAP_SPACING.component.borderRadius,
                    borderBottomRightRadius: SAP_SPACING.component.borderRadius,
                  }
                }}
              >
                <Typography sx={{ 
                  wordBreak: 'break-word',
                  fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
                  fontSize: SAP_TYPOGRAPHY.fontSize.md,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                  color: SAP_COLORS.sapGray7,
                }}>
                  {keyName}
                </Typography>
                {isEditing ? (
                  <TextField 
                    size="small" 
                    fullWidth 
                    value={editValue} 
                    onChange={(e) => setEditValue(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: SAP_SPACING.component.borderRadius,
                        fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: SAP_COLORS.sapBlue,
                          borderWidth: '2px',
                        },
                      },
                    }}
                  />
                ) : (
                  <Typography sx={{ 
                    wordBreak: 'break-word',
                    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
                    fontSize: SAP_TYPOGRAPHY.fontSize.md,
                    fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
                    color: SAP_COLORS.sapGray6,
                  }}>
                    {value}
                  </Typography>
                )}
                <Box 
                  display="flex" 
                  gap={SAP_SPACING.sm} 
                  justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                >
                  {allowEdit && (isEditing ? (
                    <>
                      <Tooltip title="Save">
                        <IconButton 
                          onClick={() => openSaveConfirm(row)} 
                          sx={{ 
                            color: SAP_COLORS.sapGreen,
                            '&:hover': {
                              backgroundColor: SAP_COLORS.sapLightGreen,
                              transform: 'scale(1.1)',
                            },
                            transition: SAP_TRANSITIONS.fast,
                          }}
                          size="small"
                        >
                          <SaveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton 
                          onClick={cancelEdit} 
                          sx={{ 
                            color: SAP_COLORS.sapGray5,
                            '&:hover': {
                              backgroundColor: SAP_COLORS.sapGray2,
                              color: SAP_COLORS.sapGray7,
                              transform: 'scale(1.1)',
                            },
                            transition: SAP_TRANSITIONS.fast,
                          }}
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Edit">
                        <IconButton 
                          onClick={() => startEdit(row)} 
                          sx={{ 
                            color: SAP_COLORS.sapBlue,
                            '&:hover': {
                              backgroundColor: SAP_COLORS.sapLightBlue,
                              transform: 'scale(1.1)',
                            },
                            transition: SAP_TRANSITIONS.fast,
                          }}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          onClick={() => openDeleteConfirm(row)} 
                          sx={{ 
                            color: SAP_COLORS.sapRed || '#d32f2f',
                            '&:hover': {
                              backgroundColor: 'rgba(211, 47, 47, 0.08)',
                              transform: 'scale(1.1)',
                            },
                            transition: SAP_TRANSITIONS.fast,
                          }}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  ))}
                </Box>
              </Box>
            );
          })}
          {rows.length === 0 && (
            <Box sx={{ 
              px: SAP_SPACING.lg, 
              py: SAP_SPACING['2xl'],
              textAlign: 'center',
              backgroundColor: SAP_COLORS.sapWhite,
            }}>
              <Typography 
                sx={{
                  color: SAP_COLORS.sapGray5,
                  fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
                  fontSize: SAP_TYPOGRAPHY.fontSize.md,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
                }}
              >
                No job settings found.
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>

      <Dialog 
        open={addOpen} 
        onClose={closeAdd} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ 
          sx: { 
            borderRadius: '12px',
            minHeight: '400px',
            maxHeight: '90vh',
            boxShadow: SAP_SHADOWS.card,
            backgroundColor: SAP_COLORS.sapWhite,
            fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
            overflow: 'hidden',
          } 
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.32)', // simple dim overlay, no blur
          }
        }}
        sx={{
          '& .MuiDialog-paper': {
            margin: '24px',
            width: 'calc(100% - 48px)',
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
          backgroundColor: SAP_COLORS.sapWhite,
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
              fontSize: '20px',
              color: SAP_COLORS.sapGray7,
              fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
              margin: 0,
            }}
          >
            Add Job Setting
          </Typography>
          <IconButton 
            onClick={closeAdd} 
            sx={{ 
              color: SAP_COLORS.sapGray5,
              padding: '8px',
              '&:hover': {
                backgroundColor: SAP_COLORS.sapGray2,
                color: SAP_COLORS.sapGray7,
              },
              transition: 'all 0.2s ease',
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        
        {/* Content */}
        <Box sx={{
          px: 3,
          py: 3,
          backgroundColor: SAP_COLORS.sapWhite,
        }}>
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}>
            {/* Configuration Key Field */}
            <Box>
              <Typography 
                variant="body2" 
                sx={{
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                  fontSize: '14px',
                  color: SAP_COLORS.sapGray7,
                  marginBottom: 1,
                  fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
                }}
              >
                Configuration Key *
              </Typography>
              <Autocomplete
                options={keys.filter((k): k is string => typeof k === 'string')}
                getOptionLabel={(option) => (typeof option === 'string' ? option : '')}
                value={newKey || null}
                inputValue={newKey}
                onInputChange={(_, value) => setNewKey(value || '')}
                onChange={(_, newValue) => setNewKey((typeof newValue === 'string' ? newValue : newValue ?? '') || '')}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Type or select a key"
                    required
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        height: '48px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: SAP_COLORS.sapGray3,
                          borderWidth: '1px',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: SAP_COLORS.sapGray4,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: SAP_COLORS.sapBlue,
                          borderWidth: '2px',
                        },
                      },
                    }}
                  />
                )}
                fullWidth
              />
            </Box>
            
            {/* Value Field */}
            <Box>
              <Typography 
                variant="body2" 
                sx={{
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                  fontSize: '14px',
                  color: SAP_COLORS.sapGray7,
                  marginBottom: 1,
                  fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
                }}
              >
                Value
              </Typography>
              <TextField 
                placeholder="Enter the configuration value" 
                fullWidth 
                value={newValue} 
                onChange={(e) => setNewValue(e.target.value)} 
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    height: '48px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: SAP_COLORS.sapGray3,
                      borderWidth: '1px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: SAP_COLORS.sapGray4,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: SAP_COLORS.sapBlue,
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    backgroundColor: SAP_COLORS.sapWhite,
                    padding: '0 8px',
                    fontSize: '14px',
                    '&.Mui-focused': {
                      color: SAP_COLORS.sapBlue,
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
        
        {/* Footer */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          px: 3,
          py: 2.5,
          borderTop: `1px solid ${SAP_COLORS.sapGray2}`,
          backgroundColor: SAP_COLORS.sapGray1,
        }}>
          <Button 
            onClick={closeAdd} 
            variant="outlined" 
            sx={{ 
              minWidth: '100px',
              height: '40px',
              borderRadius: '8px',
              borderColor: SAP_COLORS.sapGray3,
              color: SAP_COLORS.sapGray6,
              fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
              fontSize: '14px',
              fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
              textTransform: 'none',
              '&:hover': {
                borderColor: SAP_COLORS.sapGray4,
                backgroundColor: SAP_COLORS.sapGray2,
                color: SAP_COLORS.sapGray7,
              },
              transition: 'all 0.2s ease',
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmAdd} 
            variant="contained" 
            disabled={!newKey} 
            sx={{ 
              minWidth: '120px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: SAP_COLORS.sapBlue,
              color: SAP_COLORS.sapWhite,
              fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
              fontSize: '14px',
              fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
              textTransform: 'none',
              boxShadow: '0 2px 4px rgba(0, 112, 242, 0.2)',
              '&:hover': {
                backgroundColor: SAP_COLORS.sapDarkBlue,
                boxShadow: '0 4px 8px rgba(0, 112, 242, 0.3)',
                transform: 'translateY(-1px)',
              },
              '&:disabled': {
                backgroundColor: SAP_COLORS.sapGray3,
                color: SAP_COLORS.sapGray4,
                boxShadow: 'none',
                transform: 'none',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Add Setting
          </Button>
        </Box>
      </Dialog>

      {/* Confirm Save (Edit) */}
      <Dialog open={saveConfirmOpen} onClose={() => { setSaveConfirmOpen(false); setSaveConfirmRow(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: SAP_TYPOGRAPHY.fontFamily.primary, fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>
          Save job setting?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: SAP_TYPOGRAPHY.fontFamily.primary }}>
            Do you want to save the changes to this setting?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setSaveConfirmOpen(false); setSaveConfirmRow(null); }} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmSaveEdit} sx={{ textTransform: 'none', backgroundColor: SAP_COLORS.sapBlue }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete */}
      <Dialog open={deleteConfirmOpen} onClose={() => !deleting && (setDeleteConfirmOpen(false), setDeleteConfirmRow(null))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: SAP_TYPOGRAPHY.fontFamily.primary, fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>
          Delete job setting?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: SAP_TYPOGRAPHY.fontFamily.primary }}>
            {deleteConfirmRow ? (
              <>Are you sure you want to delete the setting &quot;{deleteConfirmRow[3]}&quot;? This action cannot be undone.</>
            ) : (
              'Are you sure you want to delete this job setting?'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDeleteConfirmOpen(false); setDeleteConfirmRow(null); }} disabled={deleting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleting} sx={{ textTransform: 'none' }}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}